import { useEffect, useState } from 'react';
import type { FreeBusy } from 'zitimail-shared-types';

import { calendar } from '../api/calendar';

const FROM = 7;
const TO = 20;

/**
 * Guests' busy times across the working day, with the proposed slot laid
 * over them: a clash is visible before the invitation goes out.
 */
export function Availability({
  guests,
  start,
  end,
}: {
  guests: readonly string[];
  start: Date;
  end: Date;
}): JSX.Element | null {
  const [rows, setRows] = useState<FreeBusy[] | null>(null);
  const day = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const key = `${guests.join(',')}|${day.getTime()}`;

  useEffect(() => {
    if (guests.length === 0 || Number.isNaN(start.getTime())) {
      setRows(null);
      return;
    }
    const timer = window.setTimeout(() => {
      calendar
        .freeBusy(guests, day, new Date(day.getTime() + 86_400_000))
        .then(setRows)
        .catch(() => setRows(null));
    }, 400);
    return () => window.clearTimeout(timer);
    // Refetched for a new guest list or a new day, not for every keystroke.
  }, [key]);

  if (!rows || rows.length === 0) {
    return null;
  }
  const span = (TO - FROM) * 3_600_000;
  const origin = day.getTime() + FROM * 3_600_000;
  const pos = (t: number) => Math.max(0, Math.min(100, ((t - origin) / span) * 100));
  const slot = { left: pos(start.getTime()), right: pos(end.getTime()) };
  const clash = (busy: FreeBusy['busy']) =>
    busy.some((b) => Date.parse(b.start) < end.getTime() && Date.parse(b.end) > start.getTime());

  return (
    <div className="availability" aria-label="Guests' availability">
      <div className="avail-hours" aria-hidden="true">
        {Array.from({ length: TO - FROM + 1 }, (_, i) => (
          <span key={i} style={{ left: `${(i / (TO - FROM)) * 100}%` }}>
            {String(FROM + i).padStart(2, '0')}
          </span>
        ))}
      </div>
      {rows.map((r) => (
        <div key={r.email} className="avail-row">
          <span className={`avail-who${r.known && clash(r.busy) ? ' clash' : ''}`} title={r.email}>
            {r.email.split('@')[0]}
            {!r.known && <span className="muted"> · outside</span>}
          </span>
          <span className="avail-track">
            {r.busy.map((b) => (
              <span
                key={b.start}
                className="avail-busy"
                style={{
                  left: `${pos(Date.parse(b.start))}%`,
                  width: `${pos(Date.parse(b.end)) - pos(Date.parse(b.start))}%`,
                }}
              />
            ))}
            <span
              className="avail-slot"
              style={{ left: `${slot.left}%`, width: `${Math.max(0.6, slot.right - slot.left)}%` }}
            />
          </span>
        </div>
      ))}
      <p className="muted avail-note">
        {rows.some((r) => r.known && clash(r.busy))
          ? 'Someone is busy at the proposed time.'
          : 'Everyone in your organisation is free then.'}{' '}
        Only busy times are shown, never what they are.
      </p>
    </div>
  );
}
